import { pathProfileFunctionsWrapper } from 'app/shared/path_profile/path_profile';
import { useContext, useEffect, useState } from 'react';
import { Cartesian3, Cartographic, Math } from 'cesium';
import { ServicesContext } from 'app/items/shared/context_interface';
import { areShapesEqual } from 'app/agriculture/utils';
import { LonLatValueType } from 'app/form_config/helpers';
import _ from 'lodash';
import { DegreesToMetersApprox } from 'app/map/geometry/cesium/AsyncPrimitive/utils';
import * as turf from '@turf/helpers';
import { TerrainService } from 'app/map/services/terrain.service';
import { area, triangleGrid, bbox } from '@turf/turf';

export async function getSlope({
    polygon,
    terrainService,
}: {
    polygon?: LonLatValueType[];
    terrainService: TerrainService;
}) {
    if (!polygon || polygon.length == 0) {
        return {
            max: 'Unknown',
            average: 'Unknown',
        };
    }

    // step 1 generate triangles
    const turfPoly = turf.polygon([polygon]);
    const areaMeters = area(turfPoly);
    const areaAcres = turf.convertArea(areaMeters, 'meters', 'acres');
    // NZFAP says calculate slopes across 20m lengths, this isn't practical for large areas
    if (areaAcres > 5000) {
        return {
            max: 'Unknown',
            average: 'Unknown',
        };
    }
    const grid = triangleGrid(bbox(turfPoly), DegreesToMetersApprox * 20, {
        mask: turfPoly,
        units: 'degrees',
    });

    // step 2 get height for triangle points
    const uniqueHeightsMap = new Map();
    const triangleMap = grid.features.map((feature) =>
        feature.geometry.coordinates[0].slice(1).map((i) => {
            const cartographicPoint = Cartographic.fromDegrees(i[0], i[1]);
            const key = `${cartographicPoint.latitude}:${cartographicPoint.longitude}`;
            uniqueHeightsMap.set(key, cartographicPoint);
            return cartographicPoint;
        }),
    );

    const heights = await pathProfileFunctionsWrapper.fetchTerrainHeights(
        Array.from(uniqueHeightsMap.values()),
        terrainService.cesiumTerrainProvider,
        terrainService,
        true,
    );

    if (heights.some((x) => x.height === undefined)) {
        return {
            max: 'Unknown',
            average: 'Unknown',
        };
    }

    const heightLookup = new Map(heights.map((h) => [`${h.latitude}:${h.longitude}`, h.height]));
    triangleMap.forEach((triangle) => {
        triangle.forEach((point) => {
            point.height = heightLookup.get(`${point.latitude}:${point.longitude}`);
        });
    });

    // uncomment for debugging
    // triangleMap.forEach(triangle =>
    //     triangle.forEach(point => {
    //         ctx.services.mapService.viewer.entities.add({
    //             position: Cartesian3.fromRadians(
    //                 point.longitude,
    //                 point.latitude,
    //                 point.height
    //             ),
    //             point: {
    //                 show: true, // default
    //                 color: Color.WHITE, // default: WHITE
    //                 pixelSize: 3 // default: 1
    //             }
    //         });
    //     })
    // );

    // step 3 get slope for each triangle
    const slopes = triangleMap.map((triangle) =>
        calculateTriangleSlope(triangle[0], triangle[1], triangle[2]),
    );

    // step 4 get the max and average then text transform
    return {
        max: _.max(slopes).toFixed(1),
        average: _.mean(slopes).toFixed(1),
    };
}
function calculateTriangleSlope(p1: Cartographic, p2: Cartographic, p3: Cartographic) {
    // get the slope vector
    const pointA = Cartesian3.fromRadians(p1.longitude, p1.latitude, p1.height);
    const pointB = Cartesian3.fromRadians(p2.longitude, p2.latitude, p2.height);
    const pointC = Cartesian3.fromRadians(p3.longitude, p3.latitude, p3.height);

    const AB = new Cartesian3();
    Cartesian3.subtract(pointB, pointA, AB);

    const AC = new Cartesian3();
    Cartesian3.subtract(pointC, pointA, AC);

    const cross = new Cartesian3();
    Cartesian3.cross(AB, AC, cross);

    const result = new Cartesian3();
    Cartesian3.normalize(cross, result);

    // get the normal vector
    const pointD = Cartesian3.fromRadians(p1.longitude, p1.latitude, 100);
    const pointE = Cartesian3.fromRadians(p2.longitude, p2.latitude, 100);
    const pointF = Cartesian3.fromRadians(p3.longitude, p3.latitude, 100);

    const DE = new Cartesian3();
    Cartesian3.subtract(pointE, pointD, DE);

    const DF = new Cartesian3();
    Cartesian3.subtract(pointF, pointD, DF);

    const cross2 = new Cartesian3();
    Cartesian3.cross(DE, DF, cross2);

    const result2 = new Cartesian3();
    Cartesian3.normalize(cross2, result2);

    const between = Cartesian3.angleBetween(result, result2);
    return Math.toDegrees(between);
}

export function usePolygonSlope(polygon?: LonLatValueType[]) {
    const { terrainService } = useContext(ServicesContext);
    const [polygonSlope, setPolygonSlope] = useState({ max: '0', average: '0' });
    const [storedPolygon, setStoredPolygon] = useState<LonLatValueType[] | undefined>(polygon);
    const [isCalculating, setIsCalculating] = useState(false);

    useEffect(() => {
        if (!areShapesEqual(storedPolygon, polygon)) {
            setStoredPolygon(polygon);
        }
    }, [polygon, storedPolygon]);

    useEffect(() => {
        let active = true;
        setIsCalculating(true);

        setTimeout(() => {
            getSlope({ polygon: storedPolygon, terrainService }).then((slope) => {
                if (!active) {
                    // bail, our useEffect was recreated or the component removed during our promise execution, don't touch anything on our way out
                    return;
                }
                setIsCalculating(false);
                setPolygonSlope(slope);
            });

            return () => {
                active = false;
            };
        }, 0);
    }, [storedPolygon]);

    return { isCalculating, ...polygonSlope };
}
